import React, { useContext, useEffect, useRef, useState } from 'react'
import { ReactComponent as MagnifyingGlass } from '../../assets/media/icons/magnifying-glass.svg'
import ApplicationContext from '../../context/ApplicationContext'
import { useSearchLocation } from '../../hooks'
import styles from './AutocompleteInput.module.scss'

const AutocompleteInput = () => {
    const [applicationData] = useContext(ApplicationContext)
    const [localizations] = useState(applicationData.localizations)
    const { handleSearch } = useSearchLocation()
    const [query, setQuery] = useState('')
    const [savedLat, setSavedLat] = useState('')
    const [savedLng, setSavedLng] = useState('')
    const [autocompleteInput, setAutocompleteInput] = useState('')
    const [suggestions, setSuggestions] = useState([])
    const [showDropdown, setShowDropdown] = useState(false)
    const [selectedIndex, setSelectedIndex] = useState(-1)
    const inputRef = useRef(null)
    const dropdownRef = useRef(null)
    const listboxId = 'autocomplete-listbox'
    const [activeDescendant, setActiveDescendant] = useState('')

    // Fetch suggestions using Google's new AutocompleteSuggestion API
    const fetchSuggestions = async input => {
        if (!input || input.length < 3) {
            setSuggestions([])
            setShowDropdown(false)
            return
        }

        try {
            const { AutocompleteSuggestion } = await window.google.maps.importLibrary('places')
            const request = {
                input,
                includedPrimaryTypes: ['postal_code', 'geocode', 'establishment'],
            }

            const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request)

            // Sort suggestions to prioritize postal codes first
            const sortedSuggestions = (suggestions || []).sort((a, b) => {
                const aTypes = a.placePrediction.types || []
                const bTypes = b.placePrediction.types || []

                const aIsPostalCode = aTypes.includes('postal_code')
                const bIsPostalCode = bTypes.includes('postal_code')

                // If one is postal code and other isn't, postal code comes first
                if (aIsPostalCode && !bIsPostalCode) return -1
                if (!aIsPostalCode && bIsPostalCode) return 1

                // Both are same type, maintain original order
                return 0
            })

            setSuggestions(sortedSuggestions)
            setShowDropdown(sortedSuggestions && sortedSuggestions.length > 0)
        } catch (error) {
            console.error('Error fetching suggestions:', error)
            setSuggestions([])
            setShowDropdown(false)
        }
    }

    // Handle place selection using Google's new Place API
    const selectPlace = async suggestion => {
        try {
            const { Place } = await window.google.maps.importLibrary('places')
            const place = new Place({
                id: suggestion.placePrediction.placeId
            })

            await place.fetchFields({
                fields: ['location', 'formattedAddress']
            })

            if (place.location) {
                const lat = place.location.lat()
                const lng = place.location.lng()

                if (lat !== savedLat || lng !== savedLng) {
                    setSavedLat(lat)
                    setSavedLng(lng)
                    setQuery({
                        location: { lat, lng },
                        address: place.formattedAddress,
                    })
                    setAutocompleteInput(place.formattedAddress)
                }
            }
        } catch (error) {
            console.error('Error fetching place details:', error)
        }

        setShowDropdown(false)
        setSelectedIndex(-1)
    }

    // Handle manual search for button clicks
    const handleManualSearch = async () => {
        if (!autocompleteInput) return

        try {
            const { AutocompleteSuggestion } = await window.google.maps.importLibrary('places')
            const request = {
                input: autocompleteInput,
                includedPrimaryTypes: ['postal_code', 'geocode', 'establishment'],
            }

            const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request)

            if (suggestions?.length > 0) {
                await selectPlace(suggestions[0])
            }
        } catch (error) {
            console.error('Error with manual search:', error)
        }
    }

    // Handle input changes
    const handleFieldChange = e => {
        const value = e.target.value
        setAutocompleteInput(value)
        fetchSuggestions(value)
        setSelectedIndex(-1)
    }

    // Handle keyboard navigation
    const handleKeyDown = async e => {
        if (!showDropdown) {
            if (e.key === 'Enter') {
                await handleManualSearch()
            }
            return
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault()
                setSelectedIndex(prev => {
                    const newIndex = prev < suggestions.length - 1 ? prev + 1 : prev
                    setActiveDescendant(`option-${newIndex}`)
                    return newIndex
                })
                break
            case 'ArrowUp':
                e.preventDefault()
                setSelectedIndex(prev => {
                    const newIndex = prev > 0 ? prev - 1 : -1
                    setActiveDescendant(newIndex >= 0 ? `option-${newIndex}` : '')
                    return newIndex
                })
                break
            case 'Enter':
                e.preventDefault()
                if (selectedIndex >= 0 && suggestions[selectedIndex]) {
                    await selectPlace(suggestions[selectedIndex])
                } else {
                    await handleManualSearch()
                }
                break
            case 'Escape':
                e.preventDefault()
                setShowDropdown(false)
                setSelectedIndex(-1)
                break
            default:
                break
        }
    }

    // Scroll selected option into view
    useEffect(() => {
        if (selectedIndex >= 0 && dropdownRef.current) {
            const selectedOption = dropdownRef.current.querySelector(`#option-${selectedIndex}`)
            if (selectedOption) {
                selectedOption.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
            }
        }
    }, [selectedIndex])

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = event => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
                inputRef.current && !inputRef.current.contains(event.target)) {
                setShowDropdown(false)
                setSelectedIndex(-1)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    useEffect(() => {
        search()
    }, [query])

    const search = () => {
        if (query === '') {
            return
        }

        if (query?.location?.lng && query?.location?.lat) {
            const position = {
                lat: query?.location?.lat,
                lng: query?.location?.lng,
            }
            const address = query?.address
            handleSearch(position, address)
        }
    }

    return (
        <div className={styles.inputWrapper}>
            <div className={styles.inputWrapperHolder}>
                <input
                    placeholder={localizations.mapSearchBox.placeholder}
                    className={styles.autocompleteInput}
                    onChange={handleFieldChange}
                    onKeyDown={handleKeyDown}
                    ref={inputRef}
                    value={autocompleteInput}
                    id='search'
                    role='combobox'
                    aria-label={localizations.mapSearchBox.ariaLabel}
                    aria-autocomplete='list'
                    aria-expanded={showDropdown && suggestions.length > 0}
                    aria-controls={showDropdown && suggestions.length > 0 ? listboxId : undefined}
                    aria-activedescendant={activeDescendant}
                    autoComplete="off"
                />
                {showDropdown && suggestions.length > 0 && (
                    <div
                        ref={dropdownRef}
                        className="pac-container"
                        role='listbox'
                        id={listboxId}
                        aria-label='Search suggestions'
                    >
                        {suggestions.map((suggestion, index) => (
                            <div
                                key={suggestion.placePrediction.placeId}
                                id={`option-${index}`}
                                role='option'
                                aria-selected={index === selectedIndex}
                                className={`pac-item ${index === selectedIndex ? 'pac-item-selected' : ''}`}
                                onClick={() => selectPlace(suggestion)}
                                onKeyDown={() => {}} // Keyboard handled at combobox input level per ARIA pattern
                                onMouseEnter={() => {
                                    setSelectedIndex(index)
                                    setActiveDescendant(`option-${index}`)
                                }}
                                tabIndex={-1}
                            >
                                <span className="pac-icon pac-icon-marker"></span>
                                <span className="pac-item-query">
                                    <span className="pac-matched">
                                        {suggestion.placePrediction.structuredFormat?.mainText?.text ||
                                         suggestion.placePrediction.text?.text}
                                    </span>
                                </span>
                                {suggestion.placePrediction.structuredFormat?.secondaryText?.text && (
                                    <span className="pac-item-type">
                                        {suggestion.placePrediction.structuredFormat.secondaryText.text}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <button
                onClick={handleManualSearch}
                type='button'
                className={styles.autocompleteInput_icon}
                aria-label='Search'
            >
                <MagnifyingGlass />
            </button>
        </div>
    )
}

export default AutocompleteInput
